/**
 * Where everything goes.
 *
 * Placement is what turns a generated map into a level to play: the spawn, the
 * exits, the entities, and the order the passes run in. Supplies and lighting
 * are involved enough to live in `supplies.ts` and `lighting.ts`; this module
 * owns the rest and drives all of it from `populate`.
 */

import { bfsDistances, UNREACHABLE } from '../../core/grid';
import {
  Cell,
  type Cell2,
  type EntityKind,
  type EntitySpawn,
  type ExitPoint,
  type GeneratorKind,
  type Grid,
  type LightSpot,
  type ItemSpot,
  type RegionKind,
  type Vec2,
} from '../../core/types';
import { sealUnreachable } from './connect';
import { lightExits, lightSlides, placeLights } from './lighting';
import { buildRegionIndex, collectWalkable, countWalkableNeighbors, type GenSite } from './site';
import { planSlides, type SlidePlan } from './slides';
import { placeItems } from './supplies';

/** Fraction of the deepest reachable distance an exit must be beyond. */
const EXIT_DEPTH = 0.72;
/** Cells of map edge no exit is placed in. See `filterCandidates`. */
const EXIT_EDGE_MARGIN = 4;
/** Fraction of the deepest reachable distance an entity may spawn beyond. */
const ENTITY_DEPTH = 0.5;

/**
 * The ordinary ways down, per profile. `'slide'` is deliberately absent from
 * every row: slides are not exits this pass distributes, they are extra ones
 * placed by `slides.ts` afterwards, because they have to be somewhere findable
 * rather than somewhere deep, they are spaced against each other rather than
 * against these, and each needs its own prop, its own lighting and a real
 * `target`.
 */
const EXIT_KINDS: Record<GeneratorKind, ReadonlyArray<ExitPoint['kind']>> = {
  lobby: ['noclip', 'hole', 'noclip', 'stairwell'],
  warehouse: ['stairwell', 'door', 'hole'],
  pipes: ['hole', 'noclip', 'hole'],
  poolrooms: ['door', 'noclip'],
  hub: ['door', 'stairwell'],
  funrooms: ['door', 'noclip'],
  // Level 94 is outdoors: a door in the side of a house that is not a house,
  // and a hole in somebody's lawn.
  motion: ['door', 'hole'],

  /*
   * The two buildings. An office floor plate and a hotel both have stairs and
   * lifts, and that is how you leave a building — these were reading as
   * `lobby`, which leads with a noclip, and a noclip out of a fire-stair lobby
   * is the game ignoring the level it just built.
   */
  offices: ['stairwell', 'door', 'stairwell'],
  hotel: ['stairwell', 'door', 'stairwell', 'noclip'],

  // A plant room: you leave through the floor, or through the one door marked
  // with something nobody has explained.
  station: ['hole', 'stairwell', 'noclip'],

  // Level 6 has nothing on it and nothing written on it. A door in the dark,
  // and a stairwell you find by walking into it.
  lightsout: ['door', 'stairwell'],

  // A street: a door into a building, a hole in the road, and the stairs down
  // off a pavement into whatever is under the city.
  city: ['door', 'hole', 'stairwell'],
};

/** Region kinds an ordinary run should start in. */
const SPAWN_KINDS: ReadonlyArray<RegionKind> = ['room', 'hall', 'atrium', 'pillar'];

/** Everything `generator.ts` needs to finish a GeneratedLevel. */
export interface Placement {
  spawn: Vec2;
  spawnYaw: number;
  distanceFromSpawn: Uint16Array;
  exits: ExitPoint[];
  items: ItemSpot[];
  lights: LightSpot[];
  entitySpawns: EntitySpawn[];
  /**
   * The slides among `exits`, with the cell and wall each one was fitted to.
   * The scenery pass needs both to stand the object in the right place, and
   * neither survives into `GeneratedLevel` — by then the prop and the exit say
   * everything there is to say.
   */
  slides: SlidePlan[];
}

/**
 * Choose the spawn cell: somewhere ordinary. Not a closet, not a dead end, not
 * the middle of a pool — the player should arrive in a room that looks like
 * every other room.
 */
export function chooseSpawn(site: GenSite, regionAt: Int32Array): Cell2 {
  const { grid, rng, regions } = site;
  const ordinary = regions.filter(
    (r) => SPAWN_KINDS.includes(r.kind) && r.w * r.h >= 9 && r.w * r.h <= 400,
  );
  const order = rng.shuffle(ordinary.slice());

  for (const region of order) {
    const cx = region.x + (region.w >> 1);
    const cy = region.y + (region.h >> 1);
    // Spiral out from the middle of the room for the first cell that works.
    for (let radius = 0; radius < Math.max(region.w, region.h); radius++) {
      for (let dy = -radius; dy <= radius; dy++) {
        for (let dx = -radius; dx <= radius; dx++) {
          if (Math.max(Math.abs(dx), Math.abs(dy)) !== radius) continue;
          const x = cx + dx;
          const y = cy + dy;
          if (grid.at(x, y) !== Cell.Floor) continue;
          if (regionAt[y * grid.w + x] !== region.id) continue;
          if (countWalkableNeighbors(grid, x, y) < 3) continue;
          return { cx: x, cy: y };
        }
      }
    }
  }

  // No region qualified (a maze level, say). Take the most open cell going.
  const walk = collectWalkable(grid);
  let best = -1;
  let bestScore = -1;
  for (let k = 0; k < walk.length; k++) {
    const i = walk[k]!;
    const x = i % grid.w;
    const y = (i / grid.w) | 0;
    if (grid.cells[i] !== Cell.Floor) continue;
    const open = countWalkableNeighbors(grid, x, y);
    const centrality = 1 - (Math.abs(x - grid.w / 2) + Math.abs(y - grid.h / 2)) / (grid.w + grid.h);
    const score = open + centrality;
    if (score > bestScore) {
      bestScore = score;
      best = i;
    }
  }
  if (best < 0) best = walk[0] ?? 0;
  return { cx: best % grid.w, cy: (best / grid.w) | 0 };
}

/** Face the longest clear run from the spawn, so the first thing seen has depth. */
export function spawnFacing(grid: Grid, cell: Cell2, jitter: number): number {
  const DX = [0, 1, 0, -1];
  const DY = [-1, 0, 1, 0];
  const YAW = [0, Math.PI / 2, Math.PI, -Math.PI / 2];
  let bestDir = 0;
  let bestRun = -1;
  for (let d = 0; d < 4; d++) {
    let run = 0;
    let x = cell.cx;
    let y = cell.cy;
    while (run < 60) {
      x += DX[d]!;
      y += DY[d]!;
      if (!grid.isWalkable(x, y)) break;
      run++;
    }
    if (run > bestRun) {
      bestRun = run;
      bestDir = d;
    }
  }
  return YAW[bestDir]! + jitter;
}

/**
 * Exits, in the far quartile and spread apart. Greedy farthest-point selection:
 * take the deepest cell, then keep taking the candidate furthest from every
 * exit already placed, so two exits never share a corner of the map.
 */
export function placeExits(
  site: GenSite,
  walk: Int32Array,
  dist: Uint16Array,
  maxDist: number,
): ExitPoint[] {
  const { grid, rng, spec } = site;
  const wanted = Math.max(1, spec.exitCount);
  const kinds = EXIT_KINDS[spec.generator] ?? EXIT_KINDS.lobby;

  let threshold = Math.floor(maxDist * EXIT_DEPTH);
  let candidates = filterCandidates(site, walk, dist, threshold, EXIT_EDGE_MARGIN);
  // Relax until there is enough room to spread the exits out.
  while (candidates.length < wanted * 6 && threshold > 4) {
    threshold = Math.floor(threshold * 0.75);
    candidates = filterCandidates(site, walk, dist, threshold, EXIT_EDGE_MARGIN);
  }
  // A level with nothing off the edge at all is not one worth failing over.
  if (candidates.length === 0) candidates = filterCandidates(site, walk, dist, threshold, 0);
  if (candidates.length === 0) return [];

  const chosen: number[] = [];
  const used = new Set<number>();
  let first = candidates[0]!;
  for (const i of candidates) if (dist[i]! > dist[first]!) first = i;
  chosen.push(first);
  used.add(first);

  const minSep = Math.max(10, Math.min(grid.w, grid.h) * 0.35);
  while (chosen.length < wanted) {
    let best = -1;
    let bestScore = -1;
    for (const i of candidates) {
      if (used.has(i)) continue;
      const x = i % grid.w;
      const y = (i / grid.w) | 0;
      let nearest = Infinity;
      for (const c of chosen) {
        const cx = c % grid.w;
        const cy = (c / grid.w) | 0;
        const d = Math.hypot(x - cx, y - cy);
        if (d < nearest) nearest = d;
      }
      // Prefer separation, break ties by depth.
      const score = Math.min(nearest, minSep * 1.6) * 4 + dist[i]! / maxDist;
      if (score > bestScore) {
        bestScore = score;
        best = i;
      }
    }
    if (best < 0) break;
    chosen.push(best);
    used.add(best);
  }

  // The first exit is the level's signature kind; the rest cycle a shuffled
  // remainder so three exits are rarely three of the same thing.
  const tail = rng.shuffle(kinds.slice(1));
  return chosen.map((i, n) => ({
    id: n,
    pos: grid.cellToWorld(i % grid.w, (i / grid.w) | 0),
    kind: n === 0 || tail.length === 0 ? kinds[0]! : tail[(n - 1) % tail.length]!,
    radius: 1.6,
    target: -1,
  }));
}

function filterCandidates(
  site: GenSite,
  walk: Int32Array,
  dist: Uint16Array,
  threshold: number,
  margin: number,
): number[] {
  const { grid } = site;
  const out: number[] = [];
  for (let k = 0; k < walk.length; k++) {
    const i = walk[k]!;
    const d = dist[i]!;
    if (d === UNREACHABLE || d < threshold) continue;
    const c = grid.cells[i]!;
    if (c !== Cell.Floor && c !== Cell.Damp && c !== Cell.Open) continue;
    if (margin > 0) {
      const x = i % grid.w;
      const y = (i / grid.w) | 0;
      // Depth is measured in steps from the spawn, so on an open level the
      // deepest cells are always the four corners of the map — which is where
      // three exits in a row ended up. The far quartile is still the right
      // rule; the literal edge of the world is not part of it.
      if (Math.min(x, y, grid.w - 1 - x, grid.h - 1 - y) < margin) continue;
    }
    out.push(i);
  }
  return out;
}

/**
 * Kinds that a level may list but that generation must never place.
 *
 * `hollow` is a hallucination: the director conjures one out of low sanity and
 * reaps it when it has been looked at twice. Standing one in the world at load
 * makes it a permanent tenant that the player's mental state had nothing to do
 * with. Levels legitimately carry it in `entityKinds` — it is how the Alcove and
 * Lights Out say "this floor gets scares and no bodies" — so the guard belongs
 * here rather than in the rosters.
 *
 * Note that the pick below is *uniform*: it does not read the director's
 * `SPAWN_WEIGHT`, which lives in `entities/` and may not be imported from
 * `world/`. So a weight of zero does not protect anything at this stage, and a
 * kind that must not be placed has to be named here.
 */
const NEVER_PLACED: ReadonlySet<EntityKind> = new Set<EntityKind>(['hollow']);

/** Initial entity positions: deep, spread out, and never in sight of the spawn. */
export function placeEntities(
  site: GenSite,
  walk: Int32Array,
  dist: Uint16Array,
  maxDist: number,
): EntitySpawn[] {
  const { grid, rng, spec } = site;
  const kinds = spec.entityKinds.filter((k) => !NEVER_PLACED.has(k));
  if (spec.entityBudget <= 0 || kinds.length === 0) return [];

  const threshold = Math.max(8, Math.floor(maxDist * ENTITY_DEPTH));
  const candidates: number[] = [];
  for (let k = 0; k < walk.length; k++) {
    const i = walk[k]!;
    const d = dist[i]!;
    if (d === UNREACHABLE || d < threshold) continue;
    const c = grid.cells[i]!;
    if (c === Cell.Pillar) continue;
    candidates.push(i);
  }
  if (candidates.length === 0) return [];

  const out: EntitySpawn[] = [];
  const chosen: number[] = [];
  for (let n = 0; n < spec.entityBudget; n++) {
    let best = -1;
    let bestScore = -1;
    for (let t = 0; t < 40; t++) {
      const i = candidates[rng.int(0, candidates.length - 1)]!;
      const x = i % grid.w;
      const y = (i / grid.w) | 0;
      let nearest = Infinity;
      for (const c of chosen) {
        const d = Math.hypot(x - (c % grid.w), y - ((c / grid.w) | 0));
        if (d < nearest) nearest = d;
      }
      const score = Math.min(nearest, 40) + dist[i]! / maxDist;
      if (score > bestScore) {
        bestScore = score;
        best = i;
      }
    }
    if (best < 0) break;
    chosen.push(best);
    out.push({
      pos: grid.cellToWorld(best % grid.w, (best / grid.w) | 0),
      kind: rng.pick(kinds),
    });
  }
  return out;
}

/** Run every placement pass in order and hand back the finished set. */
export function populate(site: GenSite): Placement {
  const { grid, rng } = site;
  const regionAt = buildRegionIndex(site);
  const spawnCell = chooseSpawn(site, regionAt);
  const dist = bfsDistances(grid, [spawnCell]);
  // Connectivity repair should have left nothing stranded, but anything it did
  // miss is walled off here rather than being handed out as a supply spot.
  sealUnreachable(grid, dist, UNREACHABLE);
  const walk = collectWalkable(grid);

  let maxDist = 1;
  for (let k = 0; k < walk.length; k++) {
    const d = dist[walk[k]!]!;
    if (d !== UNREACHABLE && d > maxDist) maxDist = d;
  }

  // Kept in the original draw order so the seed still means the same level.
  const spawnYaw = spawnFacing(grid, spawnCell, rng.range(-0.35, 0.35));
  const exits = placeExits(site, walk, dist, maxDist);
  // After the ordinary exits, and scored against them: slides are the way down
  // the player is meant to find, so they go where they will be, not where the
  // depth threshold pushes everything else — and there are several, spread out,
  // so that walking off in any direction runs into one. See `slides.ts`.
  const slides = planSlides(site, regionAt, walk, dist, maxDist, exits);
  for (const s of slides) exits.push(s.exit);

  const items = placeItems(site, regionAt, walk, dist, maxDist);
  const lights = placeLights(site, walk, regionAt);
  // After both passes: the exit is the only objective the game has, and an
  // unlit one is an unmarked point on an identical floor.
  lightExits(site, lights, exits);
  lightSlides(site, lights, exits);

  return {
    spawn: grid.cellToWorld(spawnCell.cx, spawnCell.cy),
    spawnYaw,
    distanceFromSpawn: dist,
    exits,
    items,
    lights,
    entitySpawns: placeEntities(site, walk, dist, maxDist),
    slides,
  };
}
